/**
 * Copyright (c) 2025, WSO2 LLC. (https://www.wso2.com).
 *
 * WSO2 LLC. licenses this file to you under the Apache License,
 * Version 2.0 (the "License"); you may not use this file except
 * in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied. See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import {
  AsgardeoSPAClient,
  FederatedAssociation,
  HttpRequestConfig,
  HttpResponse,
  getMeFederatedAssociations as baseGetMeFederatedAssociations,
  GetMeFederatedAssociationsConfig as BaseGetMeFederatedAssociationsConfig,
} from '@asgardeo/browser';

/**
 * Configuration for the React `getMeFederatedAssociations` request.
 */
export interface GetMeFederatedAssociationsConfig extends Omit<BaseGetMeFederatedAssociationsConfig, 'fetcher'> {
  /**
   * Optional custom fetcher function. If not provided, the Asgardeo SPA client's httpClient will be used.
   */
  fetcher?: (url: string, config: RequestInit) => Promise<Response>;
  /**
   * Optional instance ID for multi-instance support. Defaults to 0.
   */
  instanceId?: number;
}

/**
 * Retrieves the identity provider accounts linked to the signed-in user, using the SPA client so the
 * request carries the user's access token.
 */
const getMeFederatedAssociations = async ({
  fetcher,
  instanceId = 0,
  ...requestConfig
}: GetMeFederatedAssociationsConfig): Promise<FederatedAssociation[]> => {
  const defaultFetcher = async (url: string, config: RequestInit): Promise<Response> => {
    const client: AsgardeoSPAClient = AsgardeoSPAClient.getInstance(instanceId);
    const httpClient: (config: HttpRequestConfig) => Promise<HttpResponse<any>> = client.httpRequest.bind(client);
    const response: HttpResponse<any> = await httpClient({
      headers: config.headers as Record<string, string>,
      method: config.method || 'GET',
      url,
    } as HttpRequestConfig);

    return {
      json: () => Promise.resolve(response.data),
      ok: response.status >= 200 && response.status < 300,
      status: response.status,
      statusText: response.statusText || '',
      text: () => Promise.resolve(typeof response.data === 'string' ? response.data : JSON.stringify(response.data)),
    } as Response;
  };

  return baseGetMeFederatedAssociations({
    ...requestConfig,
    fetcher: fetcher || defaultFetcher,
  });
};

export default getMeFederatedAssociations;
